import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { schoolService } from '../../services/api';
import { GraduationCap, ChevronLeft, ChevronDown, Loader2, Clock, Target, BarChart3, Search, BookOpen, Download } from 'lucide-react';
import api from '../../services/api';
import { useLevelsData } from '../../services/levelsService';

const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h${m > 0 ? m + 'm' : ''}`;
    return `${m}m`;
};

const formatHours = (hours) => {
    if (!hours || hours === 0) return '0h';
    if (hours < 0.1) return '< 0.1h';
    return `${hours.toFixed(1)}h`;
};

const CategorySection = ({ cat, language }) => {
    const [open, setOpen] = useState(false);
    const label = language === 'fr' ? cat.labelFr : cat.labelEn;
    const scoreColor = cat.avgScore >= 70
        ? 'text-green-600 dark:text-green-400'
        : cat.avgScore >= 50
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-red-600 dark:text-red-400';
    const scoreBg = cat.avgScore >= 70
        ? 'bg-green-100 dark:bg-green-500/20'
        : cat.avgScore >= 50
            ? 'bg-amber-100 dark:bg-amber-500/20'
            : 'bg-red-100 dark:bg-red-500/20';

    return (
        <div className="border-b border-gray-100 dark:border-white/5 last:border-0">
            {/* Category header — clickable */}
            <button
                onClick={() => setOpen(!open)}
                className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/[0.02] transition"
            >
                <div className="flex items-center gap-3">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">{label}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                        {cat.totalExams} {language === 'fr' ? 'exam.' : 'exam.'}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${scoreBg} ${scoreColor}`}>
                        {cat.avgScore}%
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:block">
                        {formatDuration(cat.totalTime)}
                    </span>
                </div>
            </button>

            {/* Expanded exam history */}
            {open && cat.history.length > 0 && (
                <div className="overflow-x-auto bg-gray-50/50 dark:bg-white/[0.01]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-white/5">
                                <th className="px-5 pl-12 py-2 font-medium">{language === 'fr' ? 'Examen' : 'Exam'}</th>
                                <th className="px-5 py-2 font-medium">Date</th>
                                <th className="px-5 py-2 font-medium text-center">Score</th>
                                <th className="px-5 py-2 font-medium text-center">{language === 'fr' ? 'Durée' : 'Duration'}</th>
                                <th className="px-5 py-2 font-medium text-center">Mode</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cat.history.map(h => (
                                <tr key={h.id} className="border-b border-gray-100/50 dark:border-white/[0.02] last:border-0">
                                    <td className="px-5 pl-12 py-2.5 text-gray-700 dark:text-gray-300 font-medium">
                                        {language === 'fr' ? h.title?.fr : h.title?.en}
                                    </td>
                                    <td className="px-5 py-2.5 text-gray-500 dark:text-gray-400">
                                        {h.date ? new Date(h.date).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA') : '-'}
                                    </td>
                                    <td className="px-5 py-2.5 text-center">
                                        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${
                                            h.score >= 70
                                                ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                                                : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
                                        }`}>
                                            {h.score}%
                                        </span>
                                    </td>
                                    <td className="px-5 py-2.5 text-center text-gray-500 dark:text-gray-400">
                                        {formatDuration(h.duration)}
                                    </td>
                                    <td className="px-5 py-2.5 text-center">
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                                            {h.mode}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const LevelSection = ({ level, levelColor, levelLabel, avgCourseProgress, scoreColor, language, studentId }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 overflow-hidden">
            {/* Level header — clickable */}
            <button
                onClick={() => setOpen(!open)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/[0.02] transition"
            >
                <div className="flex items-center gap-3">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
                    <span
                        className="px-3 py-1 rounded-full text-xs font-black text-white"
                        style={{ backgroundColor: levelColor }}
                    >
                        {levelLabel}
                    </span>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
                    {level.totalExams > 0 && (
                        <>
                            <span className="flex items-center gap-1">
                                <BarChart3 className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">{level.totalExams} {language === 'fr' ? 'exam.' : 'exam.'}</span>
                                <span className="sm:hidden">{level.totalExams}</span>
                            </span>
                            <span className={`font-bold ${scoreColor}`}>
                                {level.avgScore}%
                            </span>
                            <span className="flex items-center gap-1 hidden sm:flex">
                                <Clock className="w-3.5 h-3.5" />
                                {formatDuration(level.totalTime)}
                            </span>
                        </>
                    )}
                    {avgCourseProgress !== null && (
                        <span className="flex items-center gap-1">
                            <BookOpen className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{language === 'fr' ? 'Cours' : 'Course'}</span> {avgCourseProgress}%
                        </span>
                    )}
                    {level.hours?.totalHours > 0 && (
                        <span className="flex items-center gap-1 hidden sm:flex">
                            <Clock className="w-3.5 h-3.5 text-amber-500" />
                            {formatHours(level.hours.totalHours)}
                        </span>
                    )}
                </div>
            </button>

            {/* Expanded content */}
            {open && (
                <>
                    {/* Categories */}
                    {level.categories && level.categories.length > 0 ? (
                        <div className="border-t border-gray-100 dark:border-white/5">
                            {level.categories.map(cat => (
                                <CategorySection key={cat.key} cat={cat} language={language} />
                            ))}
                        </div>
                    ) : (
                        <div className="border-t border-gray-100 dark:border-white/5 px-5 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                            {language === 'fr' ? 'Aucun examen passé pour ce niveau' : 'No exams taken for this level'}
                        </div>
                    )}

                    {/* Course Progress */}
                    {level.courseProgress && level.courseProgress.length > 0 && (
                        <div className="border-t border-gray-100 dark:border-white/5">
                            <div className="px-5 py-3 flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    {language === 'fr' ? 'Progression Cours' : 'Course Progress'}
                                </span>
                            </div>
                            <div className="px-5 pb-4 space-y-2.5">
                                {level.courseProgress.map(mod => (
                                    <div key={mod.moduleId} className="flex items-center gap-3">
                                        <span className="text-xs text-gray-600 dark:text-gray-400 w-40 truncate flex-shrink-0" title={language === 'fr' ? mod.labelFr : mod.labelEn}>
                                            {language === 'fr' ? mod.labelFr : mod.labelEn}
                                        </span>
                                        <div className="flex-1 h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all ${
                                                    mod.progress >= 80
                                                        ? 'bg-green-500 dark:bg-green-400'
                                                        : mod.progress >= 40
                                                            ? 'bg-blue-500 dark:bg-blue-400'
                                                            : 'bg-gray-300 dark:bg-gray-600'
                                                }`}
                                                style={{ width: `${mod.progress}%` }}
                                            />
                                        </div>
                                        <span className={`text-xs font-bold w-10 text-right ${
                                            mod.progress >= 80
                                                ? 'text-green-600 dark:text-green-400'
                                                : mod.progress >= 40
                                                    ? 'text-blue-600 dark:text-blue-400'
                                                    : 'text-gray-400 dark:text-gray-500'
                                        }`}>
                                            {mod.progress}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Hours Breakdown */}
                    {level.hours && level.hours.categories && level.hours.categories.length > 0 && (
                        <div className="border-t border-gray-100 dark:border-white/5">
                            <div className="px-5 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                        {language === 'fr' ? 'Relevé d\'heures' : 'Hours Record'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400">
                                        Total: {formatHours(level.hours.totalHours)}
                                    </span>
                                    {studentId && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const url = `${api.defaults.baseURL}/hours-record.php?action=school_view&student_id=${studentId}&level_var=${level.levelVar}&lang=${language}`;
                                                window.open(url, '_blank');
                                            }}
                                            title={language === 'fr' ? 'Télécharger le relevé d\'heures (PDF)' : 'Download hours record (PDF)'}
                                            className="p-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-500/20 transition"
                                        >
                                            <Download className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                                {level.hours.categories.map(cat => (
                                    <div key={cat.number} className="flex items-center justify-between py-1">
                                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate pr-2">
                                            <span className="text-gray-400 dark:text-gray-500 mr-1">{cat.number}.</span>
                                            {language === 'fr' ? cat.labelFr : cat.labelEn}
                                        </span>
                                        <span className={`text-xs font-bold flex-shrink-0 ${
                                            cat.hours > 0
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-gray-300 dark:text-gray-600'
                                        }`}>
                                            {formatHours(cat.hours)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const SchoolStats = () => {
    const { language } = useLanguage();
    const { levelsData: levelsApiData } = useLevelsData();

    const getLevelColor = (levelVar) => {
        const lvl = (levelsApiData?.levels || []).find(l => l.var === levelVar);
        return lvl?.newBackgroundColor || '#6B7280';
    };

    const [loading, setLoading] = useState(true);
    const [school, setSchool] = useState(null);
    const [students, setStudents] = useState([]);
    const [error, setError] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');

    // Selected student detail
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentStats, setStudentStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    // Load students on mount
    useEffect(() => {
        schoolService.getStudents()
            .then(data => {
                setSchool(data.school);
                setStudents(data.students || []);
            })
            .catch(err => {
                console.error('School stats error:', err);
                setError(err.response?.data?.error || 'Erreur');
            })
            .finally(() => setLoading(false));
    }, []);

    // Load student stats when selected
    const handleSelectStudent = async (student) => {
        setSelectedStudent(student);
        setStatsLoading(true);
        setStudentStats(null);
        try {
            const data = await schoolService.getStudentStats(student.id);
            setStudentStats(data);
        } catch (err) {
            console.error('Student stats error:', err);
        } finally {
            setStatsLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedStudent(null);
        setStudentStats(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {error}
            </div>
        );
    }

    // ─── Student Detail View ───
    if (selectedStudent) {
        return (
            <div className="space-y-6">
                {/* Back + Header */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBack}
                        className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 transition"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {selectedStudent.name || selectedStudent.email}
                        </h2>
                        {selectedStudent.name && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {selectedStudent.email}
                            </p>
                        )}
                    </div>
                </div>

                {statsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                ) : studentStats && studentStats.levels?.length > 0 ? (
                    <div className="space-y-3">
                        {studentStats.levels.map(level => {
                            const levelColor = getLevelColor(level.levelVar);
                            const levelLabel = language === 'fr' ? level.labelFr : level.labelEn;
                            const avgCourseProgress = level.overallCourseProgress ?? (
                                level.courseProgress?.length > 0
                                    ? Math.round(level.courseProgress.reduce((s, m) => s + m.progress, 0) / level.courseProgress.length)
                                    : null
                            );
                            const scoreColor = level.avgScore >= 70
                                ? 'text-green-600 dark:text-green-400'
                                : level.avgScore >= 50
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-red-600 dark:text-red-400';

                            return (
                                <LevelSection
                                    key={level.levelId}
                                    level={level}
                                    levelColor={levelColor}
                                    levelLabel={levelLabel}
                                    avgCourseProgress={avgCourseProgress}
                                    scoreColor={scoreColor}
                                    language={language}
                                    studentId={selectedStudent.id}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-8 text-center">
                        <p className="text-gray-500 dark:text-gray-400">
                            {language === 'fr' ? 'Aucune donnée disponible pour cet élève.' : 'No data available for this student.'}
                        </p>
                    </div>
                )}
            </div>
        );
    }

    // ─── Student List View ───
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-500/20">
                    <GraduationCap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {language === 'fr'
                            ? `Statistiques des élèves de ${school?.name}`
                            : `Student Statistics — ${school?.name}`}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {language === 'fr'
                            ? `${students.length} élève${students.length > 1 ? 's' : ''} inscrit${students.length > 1 ? 's' : ''} avec le code ${school?.code}`
                            : `${students.length} student${students.length > 1 ? 's' : ''} registered with code ${school?.code}`}
                    </p>
                </div>
            </div>

            {/* Search */}
            {students.length > 0 && (
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'fr' ? 'Rechercher un élève...' : 'Search student...'}
                        className="w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition"
                    />
                </div>
            )}

            {/* Student list */}
            {students.length === 0 ? (
                <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-8 text-center">
                    <GraduationCap className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                        {language === 'fr'
                            ? 'Aucun élève n\'a encore utilisé le code de votre école.'
                            : 'No students have used your school code yet.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-white/5">
                                    <th className="px-5 py-3 font-medium">{language === 'fr' ? 'Élève' : 'Student'}</th>
                                    <th className="px-5 py-3 font-medium">{language === 'fr' ? 'Email' : 'Email'}</th>
                                    <th className="px-5 py-3 font-medium text-center">{language === 'fr' ? 'Achats' : 'Orders'}</th>
                                    <th className="px-5 py-3 font-medium">{language === 'fr' ? 'Premier achat' : 'First order'}</th>
                                    <th className="px-5 py-3 font-medium">{language === 'fr' ? 'Dernier achat' : 'Last order'}</th>
                                    <th className="px-5 py-3 font-medium"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.filter(s => {
                                    if (!searchQuery) return true;
                                    const q = searchQuery.toLowerCase();
                                    return s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
                                }).map(s => (
                                    <tr key={s.id} className="border-b border-gray-50 dark:border-white/[0.02] last:border-0 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition">
                                        <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">
                                            {s.name}
                                        </td>
                                        <td className="px-5 py-3 text-gray-500 dark:text-gray-400">
                                            {s.email}
                                        </td>
                                        <td className="px-5 py-3 text-center text-gray-500 dark:text-gray-400">
                                            {s.orderCount}
                                        </td>
                                        <td className="px-5 py-3 text-gray-500 dark:text-gray-400">
                                            {s.firstOrder ? new Date(s.firstOrder).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA') : '-'}
                                        </td>
                                        <td className="px-5 py-3 text-gray-500 dark:text-gray-400">
                                            {s.orderCount > 1 && s.lastOrder ? new Date(s.lastOrder).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA') : '-'}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <button
                                                onClick={() => handleSelectStudent(s)}
                                                className="px-3 py-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition"
                                            >
                                                {language === 'fr' ? 'Voir stats' : 'View stats'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchoolStats;
